import { useEffect, useMemo } from 'react';

import { useControllableState } from '../../core/hooks/useControllableState.js';

/** @internal */
export interface TooltipStateProps {
  /**
   * Delay time it takes to show the tooltip.
   */
  delay?: number;

  /** Defines if the tooltip should be open initially when used uncontrolled.
   * @defaultValue false
   */
  defaultOpen?: boolean;

  /**
   * Defines if the tooltip is open / closed in a controlled component. If
   * you bind open, you will need to react to `onOpenChange` yourself in order
   * to open and close the tooltip.
   */
  open?: boolean;

  /** onOpenChange fired when the tooltip would open. */
  onOpenChange?: (isOpen: boolean) => void;
}

const TOOLTIP_DELAY_OPEN_DEFAULT = 350;
const TOOLTIP_COOLDOWN = 500;

let rollingTooltipId = 0;
let globalWarmedUp = false;
let globalWarmUpTimeout: ReturnType<typeof setTimeout> | null = null;
let globalCooldownTimeout: ReturnType<typeof setTimeout> | null = null;

const tooltips: Record<string, (immediate: boolean) => void> = {};

/**
 * @internal
 * Controls the state of a tooltip considering global warmup and cooldown timeouts
 */
export function useTooltipState(props: TooltipStateProps) {
  const {
    delay = TOOLTIP_DELAY_OPEN_DEFAULT,
    open,
    defaultOpen,
    onOpenChange,
  } = props;

  const [isOpen, setIsOpen] = useControllableState({
    value: open,
    defaultValue: defaultOpen || false,
    onChange: onOpenChange,
  });

  const id = useMemo(() => `strato-tooltip-${++rollingTooltipId}`, []);

  const ensureTooltipEntry = () => {
    tooltips[id] = hideTooltip;
  };

  // iterate through all stored references of tooltips and close them
  // if they are not this one.
  const closeOpenTooltips = () => {
    for (const hideTooltipId in tooltips) {
      if (hideTooltipId !== id) {
        tooltips[hideTooltipId](true);
        delete tooltips[hideTooltipId];
      }
    }
  };

  const showTooltip = () => {
    closeOpenTooltips();
    ensureTooltipEntry();

    // Tooltips only show after a global warmup phase, except they are triggered
    // during the global cooldown phase, in which case they open immediately.
    globalWarmedUp = true;
    setIsOpen(true);

    if (globalWarmUpTimeout) {
      clearTimeout(globalWarmUpTimeout);
      globalWarmUpTimeout = null;
    }

    if (globalCooldownTimeout) {
      clearTimeout(globalCooldownTimeout);
      globalCooldownTimeout = null;
    }
  };

  const hideTooltip = () => {
    if (!tooltips[id]) {
      return;
    }

    setIsOpen(false);

    // when closing the tooltip, we can clear the global warmup timeout.
    if (globalWarmUpTimeout) {
      clearTimeout(globalWarmUpTimeout);
      globalWarmUpTimeout = null;
    }

    if (globalWarmedUp && tooltips[id]) {
      // If the tooltips are warmed up and there is currently a cooldown in
      // progress, clear the cooldown and restart a new one.
      if (globalCooldownTimeout) {
        clearTimeout(globalCooldownTimeout);
      }

      globalCooldownTimeout = setTimeout(() => {
        delete tooltips[id];
        globalCooldownTimeout = null;
        globalWarmedUp = false;
      }, TOOLTIP_COOLDOWN);
    }
  };

  const warmupTooltip = () => {
    closeOpenTooltips();
    ensureTooltipEntry();
    // if the tooltip has not been warmed up and there is no warmup currently in progress
    // initiate the globalWarmup process.
    if (!isOpen && !globalWarmUpTimeout && !globalWarmedUp) {
      globalWarmUpTimeout = setTimeout(() => {
        globalWarmUpTimeout = null;
        globalWarmedUp = true;
        showTooltip();
      }, delay);
    } else if (!isOpen) {
      showTooltip();
    }
  };

  useEffect(() => {
    return () => {
      if (tooltips[id]) {
        delete tooltips[id];
      }
    };
  }, [id]);

  return {
    isOpen,
    open: (immediate?: boolean) => {
      if (immediate || delay <= 0 || globalWarmedUp) {
        showTooltip();
      } else {
        warmupTooltip();
      }
    },
    close: hideTooltip,
  };
}
